import { CARD_TYPES, type WorkspaceId } from "@/shared/api/types"

/** Кабинеты из `GET /me/workspaces`, включая читателя. «Организатор» и «Администратор» добавляет фронт. */
export type RoleCabinetId = Exclude<WorkspaceId, "analytics">
export type CabinetId = RoleCabinetId | "organizer" | "administrator"

export type CabinetNavItem = { to: string; labelKey: string; icon: string; end?: boolean }
export type CabinetNavGroup = { id: string; titleKey?: string; separated?: boolean; items: CabinetNavItem[] }

export type CabinetAccess = {
  isAdmin: boolean
  /** Кабинеты пользователя в порядке ответа сервера, вместе с читателем. */
  available: RoleCabinetId[]
  canCreateEvents: boolean
  canModerateEvents: boolean
  canModerateDeposits: boolean
  canManageAssociations: boolean
  canViewEventAudit: boolean
  canViewDepositAudit: boolean
  canManageCreativeCatalog: boolean
}

export const SUMMARY_PATH = "/cabinet"
/** Вход без явного returnTo: сводка сама перенаправит в последний активный кабинет. */
export const CABINET_ENTRY_PATH = `${SUMMARY_PATH}?entry=1`
export const LAST_CABINET_STORAGE_KEY = "nbr.activeWorkspace"

const ROLE_CABINET_IDS: readonly RoleCabinetId[] = ["reader", "writer", "union", "catalog"]

/** Временно: в каталогизации видны только обзор, НБР и аналитика. Снять флаг — вернуть полное меню. */
export const CATALOG_NAV_LIMITED: boolean = true

const CATALOG_NAV_KEEP = new Set(["/admin", "/admin/nbr-old", "/analytics"])

export const CABINET_HOME: Record<CabinetId, string> = {
  reader: "/account",
  writer: "/writer",
  organizer: "/portal/events/created",
  union: "/union",
  catalog: "/admin",
  administrator: "/admin/users",
}

export const CABINET_ICONS: Record<CabinetId, string> = {
  reader: "ri-book-open-line",
  writer: "ri-quill-pen-line",
  organizer: "ri-calendar-event-line",
  union: "ri-team-line",
  catalog: "ri-database-2-line",
  administrator: "ri-shield-user-line",
}

export const isRoleCabinetId = (id: string): id is RoleCabinetId => (ROLE_CABINET_IDS as readonly string[]).includes(id)

export const isCabinetId = (id: string): id is CabinetId => id === "organizer" || id === "administrator" || isRoleCabinetId(id)

export const showOrganizer = (access: Pick<CabinetAccess, "canCreateEvents" | "canModerateEvents">) =>
  access.canCreateEvents || access.canModerateEvents

export const showAdministrator = (access: Pick<CabinetAccess, "isAdmin">) => access.isAdmin

/** Домой организатору: список созданных, а если создавать нельзя — модерация. */
export const organizerHome = (access: Pick<CabinetAccess, "canCreateEvents">) =>
  access.canCreateEvents ? "/portal/events/created" : "/portal/events/moderation"

/** Чипы ролей нужны, когда кроме читателя есть ещё кабинет. */
export const hasWorkCabinets = (available: RoleCabinetId[], extra: boolean) =>
  extra || available.some((id) => id !== "reader")

export const roleCabinetsFromWorkspaces = (items: { id: string }[] | undefined): RoleCabinetId[] =>
  (items ?? []).map((item) => item.id).filter(isRoleCabinetId)

/** Модерация депозитов и реестр: у Союза, иначе в каталогизации. */
const depositHost = (available: RoleCabinetId[]): CabinetId | null => {
  if (available.includes("union")) return "union"
  if (available.includes("catalog")) return "catalog"
  return null
}

const withGroup = (groups: CabinetNavGroup[], group: CabinetNavGroup) => (group.items.length ? [...groups, group] : groups)

/** Публичные разделы сайта внутри кабинета. Адреса те же, что у гостя. */
export const SECTION_ITEMS: readonly CabinetNavItem[] = [
  { to: "/catalog", labelKey: "nav.catalog", icon: "ri-book-open-line" },
  { to: "/events", labelKey: "nav.events", icon: "ri-calendar-event-line" },
  { to: "/news", labelKey: "nav.news", icon: "ri-newspaper-line" },
  { to: "/writers", labelKey: "nav.writers", icon: "ri-team-line" },
]

const SECTIONS_GROUP: CabinetNavGroup = {
  id: "sections",
  titleKey: "workspaces.groups.sections",
  items: [...SECTION_ITEMS],
}

const SECTION_ROOTS = ["/catalog", "/events", "/news", "/writers", "/search", "/query"] as const

const CABINET_ROOTS = ["/cabinet", "/account", "/claim", "/writer", "/union", "/analytics", "/portal", "/admin"] as const

const matchesRoot = (pathname: string, root: string) => pathname === root || pathname.startsWith(`${root}/`)

/** Карточка сущности каталога: `/person/:id` и остальные типы из `CARD_TYPES`. */
export const isEntityCardPath = (pathname: string) =>
  CARD_TYPES.some((type) => {
    const prefix = `/${type}/`
    return pathname.startsWith(prefix) && pathname.length > prefix.length
  })

/** Каталог, мероприятия, новости, писатели и их карточки. `/writer` сюда не входит. */
export const isSectionPath = (pathname: string) =>
  SECTION_ROOTS.some((root) => matchesRoot(pathname, root)) || isEntityCardPath(pathname)

/** Рабочие адреса кабинета: шапка кабинета нужна и до подтверждения сессии. */
export const isCabinetPath = (pathname: string) => CABINET_ROOTS.some((root) => matchesRoot(pathname, root))

/** Какой пункт «Разделов» подсветить. Карточка сущности остаётся в «Каталоге». */
const sectionItemTo = (pathname: string): string | null => {
  if (!isSectionPath(pathname)) return null
  if (matchesRoot(pathname, "/events")) return "/events"
  if (matchesRoot(pathname, "/news")) return "/news"
  if (matchesRoot(pathname, "/writers")) return "/writers"
  return "/catalog"
}

const withSections = (groups: CabinetNavGroup[]): CabinetNavGroup[] => {
  if (!groups.length) return [SECTIONS_GROUP]
  return [...groups, { ...SECTIONS_GROUP, separated: true }]
}

const organizerItems = (access: CabinetAccess): CabinetNavItem[] => {
  const items: CabinetNavItem[] = []
  if (access.canCreateEvents) {
    items.push({ to: "/portal/events/created", labelKey: "events.portal.created", icon: "ri-draft-line" })
    items.push({ to: "/portal/events/create", labelKey: "events.portal.create", icon: "ri-add-circle-line" })
    items.push({ to: "/portal/events/attendance", labelKey: "events.portal.attendance", icon: "ri-user-follow-line" })
  }
  if (access.canModerateEvents) {
    items.push({ to: "/portal/events/moderation", labelKey: "events.portal.moderation", icon: "ri-shield-check-line" })
  }
  return items
}

const depositStaffItems = (id: CabinetId, access: CabinetAccess): CabinetNavItem[] => {
  if (depositHost(access.available) !== id) return []
  const items: CabinetNavItem[] = [
    { to: "/portal/deposit/registry", labelKey: "deposits.portal.registry", icon: "ri-book-2-line" },
  ]
  if (access.canModerateDeposits) {
    items.push({ to: "/portal/deposit/moderation", labelKey: "deposits.portal.moderation", icon: "ri-shield-check-line" })
  }
  return items
}

const roleNav = (id: CabinetId, access: CabinetAccess): CabinetNavGroup[] => {
  const deposits = depositStaffItems(id, access)

  switch (id) {
    case "reader":
      return [
        {
          id: "reader",
          items: [
            { to: "/account", labelKey: "workspaces.nav.overview", icon: "ri-dashboard-3-line", end: true },
            { to: "/portal/events/registrations", labelKey: "events.portal.registrations", icon: "ri-ticket-2-line" },
            { to: "/account/collections", labelKey: "account.tabs.collections", icon: "ri-bookmark-line" },
            { to: "/account/notifications", labelKey: "nav.notifications", icon: "ri-notification-3-line" },
            { to: "/account/profile", labelKey: "workspaces.nav.anketa", icon: "ri-user-line" },
          ],
        },
      ]
    case "writer":
      return [
        {
          id: "writer",
          items: [
            { to: "/writer", labelKey: "writer.nav.overview", icon: "ri-dashboard-3-line", end: true },
            { to: "/writer/profile", labelKey: "writer.nav.profile", icon: "ri-profile-line" },
            { to: "/writer/portfolio", labelKey: "writer.nav.portfolio", icon: "ri-book-open-line" },
            { to: "/writer/awards", labelKey: "writer.nav.awards", icon: "ri-award-line" },
            { to: "/writer/application", labelKey: "writer.nav.application", icon: "ri-file-user-line" },
            { to: "/portal/deposit/my", labelKey: "deposits.portal.my", icon: "ri-folder-user-line" },
            { to: "/portal/deposit/apply", labelKey: "deposits.portal.apply", icon: "ri-add-circle-line" },
          ],
        },
      ]
    case "organizer":
      return [{ id: "organizer", items: organizerItems(access) }]
    case "union":
      return withGroup(
        [
          {
            id: "union",
            items: [
              { to: "/union", labelKey: "union.nav.overview", icon: "ri-dashboard-3-line", end: true },
              { to: "/union/applications", labelKey: "union.nav.applications", icon: "ri-inbox-line" },
              { to: "/union/members", labelKey: "union.nav.members", icon: "ri-group-line" },
              { to: "/union/authors", labelKey: "union.nav.authors", icon: "ri-user-add-line" },
              { to: "/union/works", labelKey: "union.nav.works", icon: "ri-checkbox-circle-line" },
              { to: "/union/structure", labelKey: "union.nav.structure", icon: "ri-organization-chart" },
            ],
          },
        ],
        { id: "deposits", titleKey: "nav.deposits", items: deposits },
      )
    case "catalog": {
      const items: CabinetNavItem[] = [
        { to: "/admin", labelKey: "admin.nav.overview", icon: "ri-dashboard-3-line", end: true },
        { to: "/admin/entities", labelKey: "admin.nav.entities", icon: "ri-database-2-line" },
        { to: "/admin/nbr-old", labelKey: "admin.nav.nbrOld", icon: "ri-history-line" },
        { to: "/admin/merge", labelKey: "admin.nav.merge", icon: "ri-git-merge-line" },
        { to: "/admin/errors", labelKey: "admin.nav.errors", icon: "ri-error-warning-line" },
        { to: "/admin/dictionaries", labelKey: "admin.nav.dictionaries", icon: "ri-book-2-line" },
        { to: "/admin/import", labelKey: "admin.nav.import", icon: "ri-upload-2-line" },
        ...(access.canManageCreativeCatalog || access.isAdmin
          ? [{ to: "/admin/creative", labelKey: "admin.nav.creative", icon: "ri-list-check-2" }]
          : []),
        { to: "/analytics", labelKey: "workspaces.analytics", icon: "ri-bar-chart-box-line" },
      ]
      const groups: CabinetNavGroup[] = [
        {
          id: "catalog",
          items: CATALOG_NAV_LIMITED ? items.filter((item) => CATALOG_NAV_KEEP.has(item.to)) : items,
        },
      ]
      return withGroup(groups, {
        id: "deposits",
        titleKey: "nav.deposits",
        items: CATALOG_NAV_LIMITED ? [] : deposits,
      })
    }
    case "administrator":
      return [
        {
          id: "administrator",
          items: [
            { to: "/admin/users", labelKey: "admin.nav.users", icon: "ri-group-line" },
            { to: "/admin/roles", labelKey: "admin.nav.roles", icon: "ri-key-2-line" },
            { to: "/admin/associations", labelKey: "admin.nav.associations", icon: "ri-organization-chart" },
            { to: "/admin/audit", labelKey: "admin.nav.audit", icon: "ri-file-list-3-line" },
            { to: "/admin/audit/events", labelKey: "admin.nav.auditEvents", icon: "ri-calendar-check-line" },
            { to: "/admin/audit/deposits", labelKey: "admin.nav.auditDeposits", icon: "ri-shield-check-line" },
          ],
        },
      ]
    default:
      return []
  }
}

export const getCabinetNav = (id: CabinetId, access: CabinetAccess): CabinetNavGroup[] => withSections(roleNav(id, access))

/** Длина совпавшего пути пункта или -1; чем длиннее совпадение, тем точнее пункт. */
export const matchNavItem = (item: CabinetNavItem, pathname: string): number => {
  const path = item.to.split("?")[0]
  if (pathname === path) return path.length
  if (item.end) return -1
  return pathname.startsWith(`${path}/`) ? path.length : -1
}

export const findActiveItem = (groups: CabinetNavGroup[], pathname: string): CabinetNavItem | null => {
  let best: CabinetNavItem | null = null
  let bestScore = -1
  for (const group of groups) {
    for (const item of group.items) {
      const score = matchNavItem(item, pathname)
      if (score > bestScore) {
        best = item
        bestScore = score
      }
    }
  }
  if (best) return best
  const sectionTo = sectionItemTo(pathname)
  if (!sectionTo) return null
  for (const group of groups) {
    const item = group.items.find((candidate) => candidate.to === sectionTo)
    if (item) return item
  }
  return null
}

const bestScore = (groups: CabinetNavGroup[], pathname: string) => {
  const item = findActiveItem(groups, pathname)
  return item ? matchNavItem(item, pathname) : -1
}

const cabinetCandidates = (access: CabinetAccess): CabinetId[] => {
  const ids: CabinetId[] = [...access.available]
  if (!ids.includes("reader")) ids.push("reader")
  if (showOrganizer(access)) ids.push("organizer")
  if (showAdministrator(access)) ids.push("administrator")
  return ids
}

/**
 * Какой кабинет открыт по адресу. `null` — сводка.
 * Общий путь отдаётся последнему активному кабинету, если он среди совпавших.
 */
export const resolveCabinet = (
  pathname: string,
  access: CabinetAccess,
  lastActive: CabinetId | null,
): CabinetId | null => {
  if (pathname === SUMMARY_PATH || pathname.startsWith(`${SUMMARY_PATH}/`)) return null

  if (isSectionPath(pathname)) {
    const candidates = cabinetCandidates(access)
    if (lastActive && candidates.includes(lastActive)) return lastActive
    return "reader"
  }

  let top = -1
  let matched: CabinetId[] = []
  for (const id of cabinetCandidates(access)) {
    const score = bestScore(getCabinetNav(id, access), pathname)
    if (score < 0) continue
    if (score > top) {
      top = score
      matched = [id]
    } else if (score === top) {
      matched.push(id)
    }
  }
  if (matched.length) return lastActive && matched.includes(lastActive) ? lastActive : matched[0]

  const byHome = ROLE_CABINET_IDS.find(
    (id) => pathname === CABINET_HOME[id] || pathname.startsWith(`${CABINET_HOME[id]}/`),
  )
  return byHome ?? "reader"
}

/** Куда вести по ссылке «Кабинет»: последний активный, единственный кабинет или сводка. */
export const cabinetEntryPath = (
  available: RoleCabinetId[],
  lastActive: CabinetId | "profile" | null,
  organizerHomePath: string | null = null,
  administrator = false,
): string => {
  const last = lastActive === "profile" ? "reader" : lastActive
  if (last === "organizer" && organizerHomePath) return organizerHomePath
  if (last === "administrator" && administrator) return CABINET_HOME.administrator
  if (last === "reader") return CABINET_HOME.reader
  if (last && last !== "organizer" && last !== "administrator" && available.includes(last)) return CABINET_HOME[last]
  const count = available.length + (organizerHomePath ? 1 : 0) + (administrator ? 1 : 0)
  if (count <= 1) {
    if (administrator && available.length === 0 && !organizerHomePath) return CABINET_HOME.administrator
    if (organizerHomePath && available.length === 0) return organizerHomePath
    if (available.length === 1) return CABINET_HOME[available[0]]
    return CABINET_HOME.reader
  }
  return SUMMARY_PATH
}

export const readLastCabinet = (): CabinetId | null => {
  try {
    const value = window.localStorage.getItem(LAST_CABINET_STORAGE_KEY)
    if (value === "profile") return "reader"
    if (value === "analytics") return null
    return value && isCabinetId(value) ? value : null
  } catch {
    return null
  }
}

export const writeLastCabinet = (id: CabinetId) => {
  try {
    window.localStorage.setItem(LAST_CABINET_STORAGE_KEY, id)
  } catch {
    /* приватный режим браузера: запоминание просто не работает */
  }
}
